import { useState, useRef, useEffect, useCallback } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { DIVISIONS, DIVISION_LIST } from "./config/projects";
import { MODE_CONFIG, DEMO_ACCOUNTS, DEMO_PASSWORD, THEME } from "./config/theme";
import { ChadMark, ChadLogo } from "./components/ChadLogo";
import ExecDashboard from "./ExecDashboard";
import { fetchProjectData, fetchPortfolioData, invalidateCache } from "./services/data";
import { sendChatMessage } from "./services/chat";

// ─── Animation hook — triggers .go class on mount ──────────────────────────
function useGlideIn(ref) {
  useEffect(() => {
    if (!ref.current) return;
    const els = ref.current.querySelectorAll(".gli");
    // Small delay to ensure DOM is painted before triggering transitions
    const t = setTimeout(() => {
      els.forEach(el => el.classList.add("go"));
    }, 50);
    return () => clearTimeout(t);
  }, [ref]);
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function now() { const d = new Date(); return `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`; }
function timeOfDay() { const h = new Date().getHours(); return h < 12 ? "morning" : h < 17 ? "afternoon" : "evening"; }
function fmt(n) { return n >= 1e6 ? "$" + (n / 1e6).toFixed(1) + "M" : n >= 1e3 ? "$" + (n / 1e3).toFixed(0) + "K" : "$" + n.toFixed(0); }

// ─── Chart helpers ──────────────────────────────────────────────────────────
function shouldAutoChart(t) {
  if (!t?.rows?.length || t.rows.length < 2 || t.rows.length > 20) return false;
  return t.headers.some((_, i) => t.rows.every(r => !isNaN(parseFloat(r[i]))));
}
function autoChartConfig(t) {
  if (!t) return null;
  const xIdx = t.headers.findIndex((_, i) => t.rows.some(r => isNaN(parseFloat(r[i]))));
  const yIdx = t.headers.findIndex((_, i) => i !== xIdx && t.rows.every(r => !isNaN(parseFloat(r[i]))));
  if (xIdx === -1 || yIdx === -1) return null;
  return { type: "bar", xKey: t.headers[xIdx], yKey: t.headers[yIdx], yLabel: t.headers[yIdx] };
}

const CHART_COLORS = ["#d4a04a", "#e8b44a", "#a07820", "#f0c860", "#7a5a18", "#d4a840", "#b8821e"];

const STATUS_STYLE = {
  "Approved": { bg: "rgba(180,140,30,0.2)", color: "var(--accent)" },
  "Pending": { bg: "rgba(200,190,170,0.12)", color: "var(--text-muted)" },
  "Revise & Resubmit": { bg: "rgba(200,80,60,0.18)", color: "var(--red)" },
  "Under Review": { bg: "rgba(100,140,200,0.18)", color: "#7ea0c8" },
  "Closed": { bg: "rgba(100,180,120,0.18)", color: "var(--green)" },
  "Open": { bg: "rgba(230,140,50,0.18)", color: "var(--amber)" },
  "Draft": { bg: "rgba(150,150,150,0.15)", color: "var(--text-faint)" },
  "Active": { bg: "rgba(100,180,120,0.18)", color: "var(--green)" },
  "Executed": { bg: "rgba(180,140,30,0.2)", color: "var(--accent)" },
  "Preconstruction": { bg: "rgba(100,140,200,0.18)", color: "#7ea0c8" },
};

function StatusBadge({ value }) {
  const s = STATUS_STYLE[value] || { bg: "rgba(255,255,255,0.08)", color: "var(--text-faint)" };
  return <span style={{ background: s.bg, color: s.color, padding: "2px 9px", fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: 1, borderRadius: 2, whiteSpace: "nowrap" }}>{String(value).toUpperCase()}</span>;
}

function ResultTable({ headers, rows }) {
  const SV = new Set(Object.keys(STATUS_STYLE).map(v => v.toLowerCase()));
  return (
    <div style={{ overflowX: "auto", marginTop: 14 }}>
      <table className="result-table">
        <thead><tr>{headers.map(h => <th key={h}>{h.toUpperCase()}</th>)}</tr></thead>
        <tbody>{rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => {
              const s = String(cell ?? "—");
              return <td key={j}>{SV.has(s.toLowerCase()) ? <StatusBadge value={cell} /> : s}</td>;
            })}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function ResultChart({ chartConfig, table }) {
  if (!chartConfig || !table) return null;
  const { type, xKey, yKey, yLabel } = chartConfig;
  const headers = table.headers.map(h => h.toLowerCase());
  const xIdx = headers.indexOf((xKey || "").toLowerCase());
  const yIdx = headers.indexOf((yKey || "").toLowerCase());
  if (xIdx === -1 || yIdx === -1) return null;
  const data = table.rows.map(r => ({ [xKey]: r[xIdx], [yKey]: parseFloat(r[yIdx]) || 0 }));
  const H = 260;
  const tip = { background: "var(--card)", border: "1px solid var(--border)", color: "var(--text-body)", fontSize: 12, fontFamily: "IBM Plex Mono" };
  if (type === "pie") return (
    <div style={{ marginTop: 16, width: "100%", height: H, overflow: "hidden" }}>
      <ResponsiveContainer width="100%" height={H}>
        <PieChart>
          <Pie data={data} dataKey={yKey} nameKey={xKey} cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
            {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
          </Pie>
          <Tooltip contentStyle={tip} /><Legend wrapperStyle={{ fontSize: 11, color: "var(--text-faint)" }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
  return (
    <div style={{ marginTop: 16, width: "100%", height: H, overflow: "hidden" }}>
      <ResponsiveContainer width="100%" height={H}>
        <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: data.length > 5 ? 40 : 20 }}>
          <XAxis dataKey={xKey} tick={{ fill: "var(--text-faint)", fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "var(--border)" }} tickLine={false} angle={data.length > 5 ? -35 : 0} textAnchor={data.length > 5 ? "end" : "middle"} interval={0} />
          <YAxis tick={{ fill: "var(--text-faint)", fontSize: 10, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} label={yLabel ? { value: yLabel, angle: -90, position: "insideLeft", fill: "var(--text-faint)", fontSize: 10 } : undefined} />
          <Tooltip contentStyle={tip} />
          <Bar dataKey={yKey} radius={[2, 2, 0, 0]}>{data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}</Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ChartToggle({ showChart, onToggle }) {
  const btn = (lbl, active, fn) => <button onClick={fn} style={{ padding: "4px 12px", fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", background: active ? "var(--accent-dim)" : "transparent", border: `1px solid ${active ? "var(--accent)" : "var(--border)"}`, color: active ? "var(--accent-text)" : "var(--text-faint)", cursor: "pointer", borderRadius: 8, letterSpacing: 1 }}>{lbl}</button>;
  return <div style={{ display: "flex", gap: 6, marginTop: 12 }}>{btn("TABLE", !showChart, () => onToggle(false))}{btn("CHART", showChart, () => onToggle(true))}</div>;
}

// ─── Main App ───────────────────────────────────────────────────────────────
export default function App() {
  // Auth state
  const [screen, setScreen] = useState("login"); // login | pick | app
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [darkMode, setDarkMode] = useState(false);

  // Project state
  const [activeDivision, setActiveDivision] = useState("gc");
  const [activeProject, setActiveProject] = useState("sunset");
  const [railOpen, setRailOpen] = useState(false);

  // Data state — live only, no sample fallback
  const [datasets, setDatasets] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState(null);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const chatRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll chat to bottom
  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTo({ top: chatRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  // Current project/division lookups
  const division = DIVISIONS[activeDivision];
  const project = division?.projects[activeProject];

  // ── Login ─────────────────────────────────────────────────────────────────
  function handleLogin(email, password) {
    const account = DEMO_ACCOUNTS[email.toLowerCase().trim()];
    if (!account) return "Account not found";
    if (password !== DEMO_PASSWORD) return "Incorrect password";
    setUser(account);
    setScreen("pick");
    return null;
  }

  // ── Role selection ────────────────────────────────────────────────────────
  function handleRoleSelect(selectedRole) {
    setRole(selectedRole);
    setScreen("app");
    // Load data for the current project
    loadProjectData(activeDivision, activeProject, selectedRole);
  }

  // ── Project selection ─────────────────────────────────────────────────────
  function selectProject(divId, projId) {
    setActiveDivision(divId);
    setActiveProject(projId);
    setMessages([]);
    setDatasets(null);
    setDrawerOpen(false);
    loadProjectData(divId, projId, role);
  }

  // ── Data loading — live Google Sheets only ────────────────────────────────
  async function loadProjectData(divId, projId, currentRole) {
    const proj = DIVISIONS[divId]?.projects[projId];
    if (!proj) return;

    // Check if project has Sheets connected
    if (proj.sheetsConnected === false) {
      setDataError(`${proj.name} doesn't have Google Sheets connected yet. Data will be available once the project sheets are shared with the CHAD service account.`);
      setDataLoading(false);
      return;
    }

    setDataLoading(true);
    setDataError(null);
    setDatasets(null);

    const result = await fetchProjectData(proj.number, currentRole || "pm");

    if (result.ok) {
      setDatasets(result.datasets);
      const dsCount = Object.keys(result.datasets).length;
      setMessages([{
        role: "chad",
        isWelcome: true,
        text: `${proj.name} — ${dsCount} datasets loaded.`,
        datasetCount: dsCount,
        ts: now(),
      }]);
    } else {
      setDataError(result.error);
    }
    setDataLoading(false);
  }

  // ── Send message ──────────────────────────────────────────────────────────
  async function handleSend(q) {
    const question = q || input.trim();
    if (!question || loading || !datasets) return;

    setInput("");
    setMessages(prev => [...prev, { role: "user", text: question, ts: now() }]);
    setLoading(true);

    try {
      const modeConfig = MODE_CONFIG[role] || MODE_CONFIG.pm;
      const response = await sendChatMessage({
        question,
        datasets,
        mode: role,
        modeInstruction: modeConfig.systemInstruction,
        companyName: division ? `${division.company} — ${division.division}` : "CHAD Construction",
        projectName: project?.name || "All Projects",
        projectNumber: project?.number || "PORTFOLIO",
      });

      setMessages(prev => [...prev, {
        role: "chad",
        text: response.answer || "Done.",
        table: response.table || null,
        chartConfig: response.chartConfig || null,
        insight: response.insight || null,
        followUpSuggestions: response.followUpSuggestions || [],
        ts: now(),
      }]);
    } catch {
      setMessages(prev => [...prev, {
        role: "chad",
        text: "Connection error — please try again.",
        ts: now(),
      }]);
    }
    setLoading(false);
  }

  // ── Role switching ────────────────────────────────────────────────────────
  function switchRole(newRole) {
    setRole(newRole);
    setMessages([]);
    loadProjectData(activeDivision, activeProject, newRole);
  }

  // ── Render ────────────────────────────────────────────────────────────────
  const appClass = `app${darkMode ? " dark" : ""}`;

  // LOGIN SCREEN
  if (screen === "login") return <LoginScreen onLogin={handleLogin} />;

  // ROLE PICKER
  if (screen === "pick") return (
    <div className={appClass}>
      <RolePicker
        user={user}
        onSelect={handleRoleSelect}
        onBack={() => setScreen("login")}
      />
    </div>
  );

  // MAIN APP
  const modeConfig = MODE_CONFIG[role] || MODE_CONFIG.pm;
  const chips = modeConfig.chips || [];
  const userColor = THEME.userColors[user?.color || "default"];

  return (
    <div className={appClass}>
      {/* Topbar */}
      <header className="topbar">
        <div className="topbar-left">
          <ChadLogo markSize={28} wordSize={18} gap={10} color="#ede9e0" />
          <div className="topbar-sep desktop-only" />
          <span className="topbar-project desktop-only" style={{ color: "var(--topbar-muted)" }}>
            {project?.name || "Select project"}
          </span>
          <button className="mobile-project-btn mobile-only" onClick={() => setDrawerOpen(true)}>
            <span className="mobile-project-dot" style={{ background: division?.color || "var(--accent)" }} />
            {project?.name || "Select project"}
          </button>
        </div>
        <div className="topbar-right">
          {user?.roles.map(r => (
            <button key={r} className={`topbar-pill${role === r ? " active" : ""}`}
              onClick={() => switchRole(r)}>
              {MODE_CONFIG[r]?.short}
            </button>
          ))}
          <div className="topbar-user desktop-only">
            <div className="topbar-avatar" style={{ background: userColor.bg, border: `1.5px solid ${userColor.border}`, color: userColor.text }}>
              {user?.initials}
            </div>
            <span className="topbar-username" style={{ color: "var(--topbar-muted)" }}>{user?.name}</span>
          </div>
          <div className="topbar-avatar mobile-only" style={{ background: userColor.bg, border: `1.5px solid ${userColor.border}`, color: userColor.text }}>
            {user?.initials}
          </div>
          <button className="theme-toggle" onClick={() => setDarkMode(d => !d)}>
            {darkMode ? "\u25D1" : "\u25D0"}
          </button>
          <button className="topbar-signout desktop-only" onClick={() => { setScreen("login"); setUser(null); setRole(null); setMessages([]); setDatasets(null); }}>
            Sign out
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}
        activeDivision={activeDivision} activeProject={activeProject} onSelect={selectProject} />

      {/* Body */}
      <div className="app-body">
        {/* Rail sidebar */}
        <Sidebar
          open={railOpen}
          onToggle={() => setRailOpen(o => !o)}
          activeDivision={activeDivision}
          activeProject={activeProject}
          onSelect={selectProject}
        />

        {/* Main content */}
        <main className="main">
          {/* Data loading state */}
          {dataLoading && (
            <div className="loading-screen">
              <ChadMark size={48} />
              <div className="loading-text">Loading {project?.name}...</div>
            </div>
          )}

          {/* Data error state */}
          {dataError && !dataLoading && (
            <div className="loading-screen">
              <ChadMark size={48} />
              <div className="loading-error">{dataError}</div>
              <button className="loading-retry" onClick={() => loadProjectData(activeDivision, activeProject, role)}>
                Retry
              </button>
            </div>
          )}

          {/* Executive mode — full customizable dashboard */}
          {datasets && !dataLoading && role === "exec" && (
            <ExecDashboard datasets={datasets} onAsk={handleSend} messages={messages} loading={loading} />
          )}

          {/* PM and Field modes — KPI strip + chat */}
          {datasets && !dataLoading && role !== "exec" && (
            <>
              {/* KPI strip for PM mode */}
              {role === "pm" && <KpiStrip datasets={datasets} />}

              {/* Chat area */}
              <div className="chat-area" ref={chatRef}>
                {messages.map((msg, i) => (
                  <ChatMessage key={i} msg={msg} user={user} userColor={userColor}
                    project={project} division={division}
                    onFollowUp={handleSend} onChip={handleSend} />
                ))}
                {loading && (
                  <div className="msg">
                    <div className="msg-avatar"><ChadMark size={30} /></div>
                    <div className="msg-content">
                      <div className="msg-bubble msg-bubble-chad">
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div className="typing-dots">
                            <div className="typing-dot" />
                            <div className="typing-dot" />
                            <div className="typing-dot" />
                          </div>
                          <span className="typing-text">Thinking...</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Chips */}
              {messages.length <= 1 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "0 24px 8px" }}>
                  {chips.map(c => (
                    <button key={c} className="chip" onClick={() => handleSend(c)} disabled={loading}>{c}</button>
                  ))}
                </div>
              )}

              {/* Input bar */}
              <div className="input-bar">
                <input className="input-field" ref={inputRef}
                  placeholder={modeConfig.placeholder.replace("{project}", project?.name || "your project")}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleSend()}
                  disabled={loading}
                />
                <button className="send-btn" onClick={() => handleSend()} disabled={loading || !input.trim()}>
                  Send
                </button>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

// ─── Login Screen Component ─────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("hemanth@ferrocrete.com");
  const [password, setPassword] = useState("demo1234");
  const [error, setError] = useState("");
  const screenRef = useRef(null);
  useGlideIn(screenRef);

  function handleSubmit() {
    const err = onLogin(email, password);
    if (err) setError(err);
  }

  return (
    <div className="login-screen" ref={screenRef}>
      <div className="gli" style={{ transitionDelay: ".05s", marginBottom: 16 }}>
        <ChadMark size={56} />
      </div>
      <div className="gli" style={{ transitionDelay: ".12s", marginBottom: 4 }}>
        <span style={{ fontFamily: "'Playfair Display',serif", fontStyle: "italic", fontWeight: 500, fontSize: 28, color: "#ede9e0", letterSpacing: 1 }}>Chad</span>
      </div>
      <div className="gli login-tag" style={{ transitionDelay: ".18s" }}>
        Your project data, one question away
      </div>
      <div className="gli login-form" style={{ transitionDelay: ".24s" }}>
        <div className="field-group">
          <div className="field-label">Email</div>
          <input className="field-input" type="email" placeholder="you@company.com" value={email}
            onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSubmit()} />
        </div>
        <div className="field-group">
          <div className="field-label">Password</div>
          <input className="field-input" type="password" placeholder="Password" value={password}
            onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSubmit()} />
        </div>
        <div className="login-error">{error}</div>
        <button className="login-btn" onClick={handleSubmit}>Sign in</button>
      </div>
      <div className="gli login-hint" style={{ transitionDelay: ".35s" }}>
        <b>hemanth@ferrocrete.com</b> · Admin<br />
        <b>raz@ferrocrete.com</b> · PM<br />
        <b>shant@ferrocrete.com</b> · Field<br />
        Password: demo1234
      </div>
    </div>
  );
}

// ─── Role Picker ────────────────────────────────────────────────────────────
function RolePicker({ user, onSelect, onBack }) {
  const screenRef = useRef(null);
  useGlideIn(screenRef);
  const icons = {
    exec: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7ea0c8" strokeWidth="1.5"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
    pm: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d4a04a" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
    field: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3a7a56" strokeWidth="1.5"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="22"/></svg>,
  };
  const bgColors = { exec: "rgba(126,160,200,.1)", pm: "rgba(212,160,74,.08)", field: "rgba(58,122,86,.08)" };

  return (
    <div className="pick-screen" ref={screenRef}>
      <div className="gli pick-hello" style={{ transitionDelay: ".05s" }}>Welcome back,</div>
      <div className="gli pick-name" style={{ transitionDelay: ".1s" }}>{user?.name}</div>
      <div className="gli pick-prompt" style={{ transitionDelay: ".15s" }}>How would you like to work today?</div>
      <div className="pick-grid">
        {user?.roles.map((r, i) => (
          <button key={r} className="gli pick-card" style={{ transitionDelay: `${0.2 + i * 0.08}s` }} onClick={() => onSelect(r)}>
            <div className="pick-card-icon" style={{ background: bgColors[r] }}>{icons[r]}</div>
            <div className="pick-card-name">{MODE_CONFIG[r]?.label}</div>
            <div className="pick-card-desc">{
              r === "exec" ? "Portfolio health, financials" :
              r === "pm" ? "RFIs, submittals, schedules" :
              "Quick answers, voice-first"
            }</div>
          </button>
        ))}
      </div>
      <button className="gli pick-back" style={{ transitionDelay: ".44s" }} onClick={onBack}>
        Back to sign in
      </button>
    </div>
  );
}

// ─── Sidebar ────────────────────────────────────────────────────────────────
function Sidebar({ open, onToggle, activeDivision, activeProject, onSelect }) {
  return (
    <div className={`rail${open ? " open" : ""}`}>
      <button className="rail-toggle" onClick={onToggle}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-faint)" strokeWidth="1.5" strokeLinecap="round">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>

      {/* Collapsed: project code tiles */}
      <div className="rail-dots">
        {DIVISION_LIST.map((div, di) => (
          <div key={div.id}>
            {di > 0 && <div className="rail-sep" />}
            {Object.values(div.projects).map(proj => {
              const isActive = activeDivision === div.id && activeProject === proj.id;
              return (
                <button key={proj.id} className={`rail-dot${isActive ? " active" : ""}`}
                  onClick={() => onSelect(div.id, proj.id)} title={proj.name}>
                  {proj.code}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Expanded */}
      <div className="rail-expanded">
        {DIVISION_LIST.map(div => (
          <div key={div.id}>
            <div className="rail-label" style={{ color: div.color }}>{div.division}</div>
            {Object.values(div.projects).map(proj => {
              const isActive = activeDivision === div.id && activeProject === proj.id;
              return (
                <button key={proj.id} className={`rail-item${isActive ? " active" : ""}`}
                  onClick={() => onSelect(div.id, proj.id)}>
                  <div className="rail-item-dot" style={{ background: isActive ? div.color : "var(--text-faint)" }} />
                  {proj.name}
                </button>
              );
            })}
          </div>
        ))}
        <div className="rail-footer">
          <a href="https://hemanth-portfolio-orcin.vercel.app/" target="_blank" rel="noreferrer">Built by Hemanth</a>
        </div>
      </div>
    </div>
  );
}

// ─── Mobile Drawer ──────────────────────────────────────────────────────────
function MobileDrawer({ open, onClose, activeDivision, activeProject, onSelect }) {
  return (
    <>
      <div className={`drawer-overlay${open ? " open" : ""}`} onClick={onClose} />
      <div className={`drawer${open ? " open" : ""}`}>
        <div className="drawer-header">
          <span className="drawer-title">Select project</span>
          <button className="drawer-close" onClick={onClose}>&times;</button>
        </div>
        <div className="drawer-body">
          {DIVISION_LIST.map(div => (
            <div key={div.id}>
              <div className="drawer-div-label" style={{ color: div.color }}>{div.division}</div>
              {Object.values(div.projects).map(proj => {
                const isActive = activeDivision === div.id && activeProject === proj.id;
                return (
                  <button key={proj.id} className={`drawer-item${isActive ? " active" : ""}`}
                    onClick={() => onSelect(div.id, proj.id)}>
                    <span className="drawer-item-dot" style={{ background: isActive ? div.color : "var(--text-faint)" }} />
                    <div>
                      <div className="drawer-item-name">{proj.name}</div>
                      <div className="drawer-item-meta">{proj.number} · {proj.value}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ─── KPI Strip ──────────────────────────────────────────────────────────────
function KpiStrip({ datasets }) {
  const rfis = datasets?.rfis?.rows || [];
  const subs = datasets?.submittals?.rows || [];
  const cos = datasets?.changeOrders?.rows || [];
  const schedule = datasets?.schedule?.rows || [];

  const openRFIs = rfis.filter(r => r.status !== "Closed").length;
  const pendSubs = subs.filter(r => ["Pending", "Under Review", "Revise & Resubmit"].includes(r.status)).length;
  const coExp = cos.filter(r => !["Approved", "Executed"].includes(r.status))
    .reduce((s, r) => s + (parseFloat(r.submitted_amt) || 0), 0);

  // Compute SPI
  const today = new Date();
  let actSum = 0, planSum = 0;
  schedule.forEach(r => {
    const fin = new Date(r.finish), st = new Date(r.start);
    const act = parseFloat(r.pct) || 0;
    const plan = fin < today ? 100 : st > today ? 0 : Math.min(100, Math.round(((today - st) / (fin - st)) * 100));
    actSum += act;
    planSum += plan;
  });
  const spi = planSum > 0 ? (actSum / planSum).toFixed(2) : "1.00";

  const kpis = [
    { label: "Open RFIs", value: openRFIs, color: openRFIs >= 3 ? "var(--kpi-red)" : "var(--kpi-green)" },
    { label: "Pending submittals", value: pendSubs, color: pendSubs >= 3 ? "var(--kpi-amber)" : "var(--kpi-green)" },
    { label: "CO exposure", value: fmt(coExp), color: coExp > 200000 ? "var(--kpi-red)" : coExp > 50000 ? "var(--kpi-amber)" : "var(--kpi-green)" },
    { label: "SPI", value: spi, color: parseFloat(spi) < 0.85 ? "var(--kpi-red)" : parseFloat(spi) < 0.95 ? "var(--kpi-amber)" : "var(--kpi-green)", mono: true },
  ];

  return (
    <div className="kpi-strip">
      {kpis.map(k => (
        <div key={k.label} className="kpi-cell">
          <div className="kpi-value" style={{ color: k.color, ...(k.mono ? { fontFamily: "'IBM Plex Mono', monospace", fontSize: 20 } : {}) }}>{k.value}</div>
          <div className="kpi-label">{k.label}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Chat Message ───────────────────────────────────────────────────────────
function ChatMessage({ msg, user, userColor, project, division, onFollowUp, onChip }) {
  const canChart = msg.table && shouldAutoChart(msg.table);
  const chartConfig = msg.chartConfig || (canChart ? autoChartConfig(msg.table) : null);
  const [showChart, setShowChart] = useState(!!msg.chartConfig);

  // Welcome message
  if (msg.isWelcome) {
    return (
      <div className="msg">
        <div className="msg-avatar"><ChadMark size={30} /></div>
        <div className="msg-content">
          <div className="msg-bubble msg-bubble-chad">
            <div className="msg-welcome-name">{project?.name} <em>{project?.location || ""}</em></div>
            <div className="msg-welcome-meta">{project?.number} · {msg.datasetCount} datasets loaded</div>
            <div>Good {timeOfDay()}. What would you like to know?</div>
          </div>
          <div className="msg-timestamp">Chad · {msg.ts}</div>
        </div>
      </div>
    );
  }

  // User message
  if (msg.role === "user") {
    return (
      <div className="msg user">
        <div className="msg-avatar msg-avatar-user" style={{ background: userColor.bg, border: `1.5px solid ${userColor.border}`, color: userColor.text }}>
          {user?.initials}
        </div>
        <div className="msg-content">
          <div className="msg-bubble msg-bubble-user">{msg.text}</div>
          <div className="msg-timestamp">You · {msg.ts}</div>
        </div>
      </div>
    );
  }

  // Chad message
  return (
    <div className="msg">
      <div className="msg-avatar"><ChadMark size={30} /></div>
      <div className="msg-content">
        <div className="msg-bubble msg-bubble-chad">
          <p style={{ whiteSpace: "pre-line" }}>{msg.text}</p>
          {msg.table && (
            <>
              {chartConfig && <ChartToggle showChart={showChart} onToggle={setShowChart} />}
              {showChart && chartConfig
                ? <ResultChart chartConfig={chartConfig} table={msg.table} />
                : <ResultTable headers={msg.table.headers} rows={msg.table.rows} />
              }
            </>
          )}
          {msg.insight && (
            <div className="msg-insight">{msg.insight}</div>
          )}
          {msg.followUpSuggestions?.length > 0 && (
            <div className="follow-ups">
              <div className="follow-up-label">Suggested follow-ups</div>
              <div className="follow-up-row">
                {msg.followUpSuggestions.map((s, i) => (
                  <button key={i} className="follow-up-chip" onClick={() => onFollowUp(s)}>{s}</button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="msg-timestamp">Chad · {msg.ts}</div>
      </div>
    </div>
  );
}
